import { PlusIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { UserBasic } from '../../../types'
import { MessageButton, ConversationItem, LoadingSpinner } from '../common'
import { useMessageNotification, useConversations } from '../hooks'
import MessageModal from '../modals/MessageModal'

interface MessageNotificationProps {
  projectId: string
  className?: string
}

export default function MessageNotification({
  projectId,
  className = '',
}: MessageNotificationProps) {
  const [showModal, setShowModal] = useState(false)
  const [selectedRecipient, setSelectedRecipient] = useState<{
    id: string
    name: string
    email: string
  } | null>(null)
  const [showNewConversation, setShowNewConversation] = useState(false)

  const { unreadCount, refreshUnreadCount } = useMessageNotification({
    projectId,
    enablePolling: true,
    pollingInterval: 30000,
  })

  const {
    conversations,
    adminUsers,
    loading,
    loadingAdmins,
    error,
    loadConversations,
    loadAdminUsers,
    clearError,
  } = useConversations()

  const handleOpenMessages = async () => {
    await loadConversations(projectId)
    setShowNewConversation(false)
    setShowModal(true)
  }

  const handleStartNewConversation = async () => {
    await loadAdminUsers(projectId)
    setShowNewConversation(true)
  }

  const handleOpenConversation = (
    participantId: string,
    participantName: string,
    participantEmail: string
  ) => {
    setSelectedRecipient({
      id: participantId,
      name: participantName,
      email: participantEmail,
    })
    setShowModal(false)
  }

  const handleSelectAdminForNewConversation = (admin: UserBasic) => {
    setSelectedRecipient({
      id: admin.id,
      name: admin.name,
      email: admin.email,
    })
    setShowNewConversation(false)
  }

  const handleCloseMessage = () => {
    setSelectedRecipient(null)
    setShowModal(false)
    setShowNewConversation(false)
    refreshUnreadCount()
  }

  const handleRetry = () => {
    clearError()
    if (showNewConversation) {
      loadAdminUsers(projectId)
    } else {
      loadConversations(projectId)
    }
  }

  return (
    <>
      <MessageButton
        onClick={handleOpenMessages}
        unreadCount={unreadCount}
        className={className}
        title="Messages"
        size="lg"
      />

      {/* Conversations List Modal */}
      {showModal && !selectedRecipient && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-25"
              onClick={() => setShowModal(false)}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-96 overflow-hidden">
              <div className="p-4 border-b">
                <div className="flex items-center">
                  {showNewConversation && (
                    <button
                      onClick={() => setShowNewConversation(false)}
                      className="mr-2 text-gray-400 hover:text-gray-600"
                    >
                      <ArrowLeftIcon className="w-5 h-5" />
                    </button>
                  )}
                  <h3 className="text-lg font-semibold">
                    {showNewConversation ? 'Select Admin' : 'Messages'}
                  </h3>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              <div className="overflow-y-auto max-h-80">
                {error && (
                  <div className="p-4 bg-red-50 border-b border-red-200 text-red-600 text-sm">
                    {error}
                    <button onClick={handleRetry} className="ml-2 underline hover:no-underline">
                      Retry
                    </button>
                  </div>
                )}
                {showNewConversation ? (
                  // Admin selection view
                  <div>
                    {loadingAdmins ? (
                      <div className="p-8">
                        <LoadingSpinner message="Loading admins..." />
                      </div>
                    ) : adminUsers.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        <MessageButton
                          onClick={() => {}}
                          className="w-12 h-12 mx-auto mb-2 text-gray-300 cursor-default"
                          size="lg"
                        />
                        <p>No admin users available</p>
                      </div>
                    ) : (
                      adminUsers.map(admin => (
                        <button
                          key={admin.id}
                          onClick={() => handleSelectAdminForNewConversation(admin)}
                          className="w-full p-4 border-b hover:bg-gray-50 text-left"
                        >
                          <div className="font-medium text-gray-900">{admin.name}</div>
                          <div className="text-sm text-gray-500">{admin.email}</div>
                        </button>
                      ))
                    )}
                  </div>
                ) : (
                  // Conversations list view
                  <div>
                    {/* Start New Conversation Button */}
                    <button
                      onClick={handleStartNewConversation}
                      className="w-full p-4 border-b hover:bg-blue-50 text-left bg-blue-25"
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <PlusIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="font-medium text-blue-600">Start New Conversation</div>
                      </div>
                    </button>

                    {/* Existing Conversations */}
                    {loading ? (
                      <div className="p-8">
                        <LoadingSpinner message="Loading conversations..." />
                      </div>
                    ) : conversations.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        <MessageButton
                          onClick={() => {}}
                          className="w-12 h-12 mx-auto mb-2 text-gray-300 cursor-default"
                          size="lg"
                        />
                        <p>No conversations yet</p>
                        <p className="text-xs mt-1">
                          Click "Start New Conversation" above to begin
                        </p>
                      </div>
                    ) : (
                      conversations.map(conversation => (
                        <ConversationItem
                          key={conversation.participant_id}
                          participantName={conversation.participant_name}
                          participantEmail={conversation.participant_email}
                          lastMessage={conversation.last_message}
                          lastMessageAt={conversation.last_message_at}
                          unreadCount={conversation.unread_count}
                          onClick={() =>
                            handleOpenConversation(
                              conversation.participant_id,
                              conversation.participant_name,
                              conversation.participant_email
                            )
                          }
                          className="border-b"
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Individual Message Modal */}
      {selectedRecipient && (
        <MessageModal
          isOpen={true}
          onClose={handleCloseMessage}
          projectId={projectId}
          recipientId={selectedRecipient.id}
          recipientName={selectedRecipient.name}
          recipientEmail={selectedRecipient.email}
        />
      )}
    </>
  )
}
